/**
 * The address book, as the composer's recipient fields use it.
 *
 * Talks to `/contacts` and nothing else, the same way `auth.ts` talks only to
 * `/auth`. This is a separate surface from JMAP on the server -- it is not a
 * JMAP object type, it has no `accountId`, and it is scoped to the tenant
 * structurally rather than by a parameter -- so it gets its own module rather
 * than a corner of `jmap.ts`.
 *
 * # Why completion is a class and not a function
 *
 * The obvious `searchContacts(prefix)` has a bug that testing does not find,
 * because it needs two responses to arrive out of order. Type "sar", type "ah",
 * and if the response for `sar` lands after the one for `sarah` the dropdown
 * shows results for a prefix the user has already finished typing -- and does
 * so *persistently*, because nothing further is in flight to correct it. The
 * request that produces this is the common one: `sar` matches more rows, so it
 * is the slower query.
 *
 * {@link Completions} makes that unrepresentable rather than merely unlikely. A
 * superseded run resolves to `null`, so the only way to render a stale result is
 * to write code that renders `null` as an empty list -- which is what a caller
 * would do anyway. The `AbortController` on top of it is a courtesy to the
 * server, not the correctness argument; the sequence number is.
 *
 * # What must never happen here
 *
 * None of this may block typing. Every failure path below returns "no
 * suggestions" rather than throwing: an address book that is slow, unreachable,
 * or serving a proxy's HTML error page must cost the user nothing more than an
 * empty dropdown, because the field they are typing into works perfectly well
 * without it.
 */

// The transport seam. `@transport` resolves at build time to the browser
// implementation or the desktop one; nothing below can tell which. Imported
// under the name `fetch` so the call sites are untouched and this import is
// the whole of the difference. See `lib/net.types.ts`.
import { authHeaders } from "./settings";
import { request as fetch } from "@transport";

/** One address book entry. Field names as the daemon spells them on the wire. */
export interface Contact {
  id: number;
  /**
   * The normalised identity, and the thing to put in a `To:` header.
   *
   * Distinct from {@link displayAddress} for Gmail dot-and-plus spellings. Send
   * to this one; show the other.
   */
  address: string;
  /** The spelling last seen on the wire. Show this to a human. */
  displayAddress: string;
  name: string | null;
  /** `"explicit"`, `"sent"` or `"received"`. Derived, never stored. */
  tier: "explicit" | "sent" | "received";
  pinned: boolean;
  blocked: boolean;
  sentCount: number;
  sentLastAt: number | null;
  receivedCount: number;
  receivedLastAt: number | null;
  createdAt: number;
  updatedAt: number;
  /**
   * The rank this row scored under the clock that served the request.
   *
   * Deliberately never rendered and never sorted on here. The daemon documents
   * it as unstable across releases -- the whole point of ranking at query time
   * is being free to change the formula -- so a client that sorted by it would
   * quietly reorder its own dropdown on an unrelated server upgrade. The
   * server's order is the order.
   */
  score: number;
}

export interface ContactPage {
  items: Contact[];
  /** The limit actually applied, which may be lower than the one asked for. */
  limit: number;
  offset: number;
  /** Null on the `?q=` path: a completion computes no total, by design. */
  total: number | null;
}

/** Largest page the daemon will serve a completion. Clamped server-side too. */
export const MAX_COMPLETION_LIMIT = 50;

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    // Always, including on the GETs where it means nothing and on bodyless
    // writes. The daemon refuses a cookie-authenticated non-GET without it,
    // which is what stops a cross-site form from forging one, and sending it
    // unconditionally is one fewer rule to remember at each call site.
    // `authHeaders` rather than a bare content-type: it sends the same
    // content-type and adds the bearer when this build has one. A browser has
    // none and is authenticated by its cookie; a packaged build has no cookie,
    // and this line is the whole of what makes the request authenticated at
    // all. See `lib/settings.ts` for what leaving it out cost.
    headers: authHeaders(),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal ? { signal } : {}),
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      detail = (await res.json()).error ?? detail;
    } catch {
      // A proxy error page is HTML; the status line is the message.
    }
    throw new Error(detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * A recipient field's private completion channel.
 *
 * One per field. Sharing one between the To, Cc and Bcc rows would mean typing
 * in Cc cancels the dropdown open over To, which is a bug that looks like a
 * rendering glitch.
 */
export class Completions {
  #controller: AbortController | null = null;
  #seq = 0;

  /**
   * Run a completion.
   *
   * Resolves to the rows, or to `null` if a later call started first -- which
   * is the caller's signal to render nothing rather than to render these. A
   * failure of any kind is an empty list, never a rejection: see the module
   * note on not blocking typing.
   */
  async run(prefix: string, limit = 8): Promise<Contact[] | null> {
    const term = prefix.trim();
    if (term === "") {
      this.cancel();
      return [];
    }

    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;
    const mine = (this.#seq += 1);

    try {
      const clamped = Math.min(Math.max(1, limit), MAX_COMPLETION_LIMIT);
      const page = await request<ContactPage>(
        "GET",
        `/contacts?q=${encodeURIComponent(term)}&limit=${clamped}`,
        undefined,
        controller.signal,
      );
      // The guard that matters. Checked *after* the await, because that is the
      // only place the ordering can have changed.
      if (mine !== this.#seq) return null;
      // Blocked addresses are in the book so the daemon can keep score; a
      // dropdown suggesting one is offering to write to someone the user has
      // already said no to.
      return page.items.filter((c) => !c.blocked);
    } catch {
      if (mine !== this.#seq) return null;
      return [];
    }
  }

  /** Stop whatever is in flight and make its result unusable. */
  cancel(): void {
    this.#controller?.abort();
    this.#controller = null;
    this.#seq += 1;
  }
}

/**
 * The whole book, for the management view.
 *
 * `total` is a number on this path and null on the completion path; the
 * management view is the only caller that needs it, which is why the daemon
 * declines to compute one on every keystroke.
 */
export function listContacts(limit = 100, offset = 0): Promise<ContactPage> {
  return request<ContactPage>("GET", `/contacts?limit=${limit}&offset=${offset}`);
}

/**
 * Promote an address to the explicit tier, or add one that was never harvested.
 *
 * Always succeeds with the resulting row, whether it created or promoted. An
 * address already in the book keeps its accumulated counts -- the daemon is
 * explicit that a user confirming a contact is the worst possible moment to
 * discard the evidence that it matters.
 */
export function promoteContact(address: string, name?: string | null): Promise<Contact> {
  return request<Contact>("POST", "/contacts", {
    address,
    ...(name ? { name } : {}),
  });
}

/**
 * Stop suggesting one address.
 *
 * Idempotent over HTTP, and note what the daemon does *not* do for a harvested
 * contact: delete the row. A plain delete would be undone by the next message
 * from that person, so the prune would appear to work and then quietly reverse
 * itself a day later.
 */
export function removeContact(id: number): Promise<void> {
  return request<void>("DELETE", `/contacts/${id}`);
}

/** A bulk prune. The daemon refuses an unfiltered one rather than emptying the book. */
export function pruneContacts(spec: {
  ids?: number[];
  tier?: "received" | "sent" | "explicit";
  olderThanDays?: number;
}): Promise<{ removed: number }> {
  return request<{ removed: number }>("POST", "/contacts/prune", spec);
}
